import React, { useContext, useEffect, useState } from "react";
import { I18nextContext, Link, useTranslation } from "gatsby-plugin-react-i18next";
import axios from "axios";

import { GLOBAL_BANNER_URL, getBannerText, getGlobalBanner, withGuards } from "../../config/globalBanner";
import * as styles from "../../styles/modules/GlobalBanner.module.sass";

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false
};

const ICONS = {
  bell: props => (
    <svg {...iconProps} {...props}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  info: props => (
    <svg {...iconProps} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  ),
  warning: props => (
    <svg {...iconProps} {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  gift: props => (
    <svg {...iconProps} {...props}>
      <path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7ZM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z" />
    </svg>
  ),
  megaphone: props => (
    <svg {...iconProps} {...props}>
      <path d="m3 11 18-5v12L3 14v-3Z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  )
};

const ArrowIcon = props => (
  <svg {...iconProps} {...props}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const CloseIcon = props => (
  <svg {...iconProps} {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// Поддържа **удебелен текст** и [линк](/path/) в текста от конфигурацията.
const INLINE_MARKUP = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

const renderLink = (label, url, key) => {
  const isExternal = /^(https?:)?\/\//.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");

  if (isExternal) {
    return (
      <a key={key} className={styles.inlineLink} href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }

  return (
    <Link key={key} className={styles.inlineLink} to={url}>
      {label}
    </Link>
  );
};

const renderMessage = message => {
  const nodes = [];
  let lastIndex = 0;
  let match = null;

  INLINE_MARKUP.lastIndex = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = INLINE_MARKUP.exec(message)) !== null) {
    const [full, boldText, linkLabel, linkUrl] = match;

    if (match.index > lastIndex) {
      nodes.push(message.slice(lastIndex, match.index));
    }

    if (boldText) {
      nodes.push(
        <strong key={`b-${match.index}`} className={styles.bold}>
          {boldText}
        </strong>
      );
    } else {
      nodes.push(renderLink(linkLabel, linkUrl, `l-${match.index}`));
    }

    lastIndex = match.index + full.length;
  }

  if (lastIndex < message.length) {
    nodes.push(message.slice(lastIndex));
  }

  return nodes;
};

const getStorage = persistence => {
  if (typeof window === "undefined") return null;

  try {
    return persistence === "permanent" ? window.localStorage : window.sessionStorage;
  } catch (e) {
    return null;
  }
};

export const dismissKey = id => `boleron-banner-dismissed:${id}`;

// Чете се синхронно, докато се рендира — иначе банерът първо би се показал и чак
// след това би изчезнал (виждаше се като "премигване").
const wasDismissed = banner => {
  if (!banner?.dismissible) return false;

  try {
    return getStorage(banner.dismissPersistence)?.getItem(dismissKey(banner.id)) === "true";
  } catch (e) {
    return false;
  }
};

const GlobalBanner = ({ path }) => {
  const { t } = useTranslation();
  const languageContext = useContext(I18nextContext);
  const currentPath = path || languageContext?.originalPath || languageContext?.path || "/";

  // Съдържанието се чете при зареждане на страницата, за да може да се сменя само
  // с редакция на JSON-а, без нов билд. Липсващ или счупен файл = няма банер.
  const [content, setContent] = useState(null);
  // Пази само банера, затворен в текущия изглед — останалото идва от storage.
  const [closedId, setClosedId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    axios
      .get(GLOBAL_BANNER_URL)
      .then(response => {
        if (!cancelled) setContent(response?.data);
      })
      .catch(error => {
        // Тихият провал прави дебъгването невъзможно: липсващ файл, счупен JSON
        // или блокирана заявка от разширение в браузъра изглеждат еднакво.
        // eslint-disable-next-line no-console
        console.warn(`GlobalBanner: не можах да прочета ${GLOBAL_BANNER_URL} — съобщението няма да се покаже.`, error?.message);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const banner = content ? getGlobalBanner(currentPath, withGuards(content)) : null;
  const hidden = !banner || closedId === banner.id || wasDismissed(banner);

  // Диагностика само в dev/staging: иначе "не се показва" може да значи липсващ
  // файл, изключен ключ, друга страница или вече затворено съобщение.
  useEffect(() => {
    if (!content || process.env.GATSBY_ENV === "PRODUCTION" || process.env.NODE_ENV === "test") return;

    // eslint-disable-next-line no-console
    console.info("[GlobalBanner]", {
      path: currentPath,
      showGlobalBanner: content?.showGlobalBanner,
      banner: banner?.id || null,
      hidden,
      dismissedInStorage: banner ? getStorage(banner.dismissPersistence)?.getItem(dismissKey(banner.id)) : null
    });
  }, [content, currentPath, banner?.id, hidden]);

  if (hidden) return null;

  const message = getBannerText(banner.message, languageContext?.language);
  if (!message) return null;

  const ctaLabel = getBannerText(banner.cta?.label, languageContext?.language);
  const ctaUrl = banner.cta?.url;
  const Icon = ICONS[banner.icon] || (banner.icon === "none" ? null : ICONS.bell);

  const dismiss = () => {
    setClosedId(banner.id);

    try {
      getStorage(banner.dismissPersistence)?.setItem(dismissKey(banner.id), "true");
    } catch (e) {
      // Приватен режим на браузъра — банерът просто ще се появи отново.
    }
  };

  return (
    <div className={`${styles.banner} ${styles[banner.variant] || styles.info}`} role="status" aria-live="polite" data-cy-global-banner>
      <div className={styles.inner}>
        {Icon && (
          <span className={styles.iconBadge}>
            <Icon className={styles.icon} />
          </span>
        )}

        <p className={styles.message}>{renderMessage(message)}</p>

        {ctaUrl &&
          ctaLabel &&
          (banner.cta?.external ? (
            <a className={styles.cta} href={ctaUrl} target="_blank" rel="noreferrer">
              {ctaLabel}
              <ArrowIcon className={styles.ctaIcon} />
            </a>
          ) : (
            <Link className={styles.cta} to={ctaUrl}>
              {ctaLabel}
              <ArrowIcon className={styles.ctaIcon} />
            </Link>
          ))}

        {banner.dismissible && (
          <button
            type="button"
            className={styles.close}
            onClick={dismiss}
            aria-label={t("globalBannerClose", "Затвори съобщението")}
            title={t("globalBannerClose", "Затвори съобщението")}
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        )}
      </div>
    </div>
  );
};

export default GlobalBanner;
